import React from 'react';
import { Box, Grid, Paper, Typography, CircularProgress, Alert, Stack, Divider, LinearProgress, useTheme, alpha, Tooltip } from '@mui/material';
import { useReport } from '../../../hooks/useReport';
import AnimatedMetricDisplay from './AnimatedMetricDisplay';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SourceIcon from '@mui/icons-material/Source';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';

const StatCard = ({ title, value, subtext, icon: Icon, color, trend, tooltip }) => {
    const theme = useTheme();
    const iconColor = color || theme.palette.primary.main;
    const bgColor = alpha(iconColor, 0.1);

    return (
        <Paper 
            variant="outlined" 
            sx={{ 
                p: 2.5, 
                height: '100%', 
                borderRadius: 3,
                transition: 'box-shadow 0.2s, transform 0.2s',
                '&:hover': {
                    boxShadow: theme.shadows[2],
                    transform: 'translateY(-2px)'
                }
            }}
        >
            <Stack spacing={1} height="100%" justifyContent="space-between">
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                            {title}
                        </Typography>
                        {tooltip && (
                            <Tooltip title={tooltip} arrow placement="top">
                                <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
                            </Tooltip>
                        )}
                    </Box>
                    {Icon && (
                        <Box sx={{ p: 1, borderRadius: '50%', bgcolor: bgColor, display: 'flex' }}>
                            <Icon sx={{ color: iconColor, fontSize: 20 }} />
                        </Box>
                    )}
                </Box>
                <Box>
                    <Typography variant="h4" fontWeight={700} color="text.primary">
                        {value}
                    </Typography>
                    {subtext && (
                        <Stack direction="row" alignItems="center" spacing={0.5} mt={0.5}>
                            {trend === 'up' && <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />}
                            <Typography variant="caption" color="text.secondary">
                                {subtext}
                            </Typography>
                        </Stack>
                    )}
                </Box>
            </Stack>
        </Paper>
    );
};

const LinearMetric = ({ label, value, total, color }) => {
    const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
    
    return (
        <Box sx={{ py: 1 }}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2" fontWeight={500}>{label}</Typography>
                <Typography variant="body2" color="text.secondary">{value} ({percentage}%)</Typography>
            </Box>
            <LinearProgress 
                variant="determinate" 
                value={percentage} 
                sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    bgcolor: (theme) => alpha(color || theme.palette.primary.main, 0.1),
                    '& .MuiLinearProgress-bar': {
                        bgcolor: color || 'primary.main',
                        borderRadius: 4
                    }
                }} 
            />
        </Box>
    );
};

const SectionHeader = ({ title }) => (
    <Typography variant="h6" fontWeight={600} sx={{ mb: 2, mt: 4, color: 'text.primary' }}>
        {title}
    </Typography>
);

export default function IdealCandidateReport({ idealCandidate }) {
    const theme = useTheme();
    const { reportData, loading, error } = useReport(idealCandidate?.id);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress size={40} thickness={4} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box mt={2}>
                <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
                    {error || "Unable to load report data."}
                </Alert>
            </Box>
        );
    }

    if (!reportData) {
        return (
            <Box mt={2}>
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
                    No report data available for this candidate.
                </Alert>
            </Box>
        );
    }

    const safePercent = (num, total) => total > 0 ? Math.round((num / total) * 100) : 0;

    return (
        <Box sx={{ p: { xs: 1, md: 2 }, maxWidth: '1600px', margin: '0 auto' }}>
            
            <Box mb={4} display="flex" justifyContent="space-between" alignItems="flex-end">
                <Box>
                    <Typography variant="h5" fontWeight="700" gutterBottom>
                        Hiring Report
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Analytics for <strong>{reportData.job_title}</strong> • {new Date(reportData.report_start_date).toLocaleDateString()} to {new Date(reportData.report_end_date).toLocaleDateString()}
                    </Typography>
                </Box>
            </Box>

            <SectionHeader title="Pipeline Overview" />
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        title="Total Applicants" 
                        value={reportData.total_applicants}
                        subtext={`${reportData.avg_applications_per_day} per day avg`}
                        icon={PeopleIcon}
                        color={theme.palette.primary.main}
                        trend="up"
                        tooltip="Total number of candidates who applied."
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        title="Total Rejected" 
                        value={reportData.declined_count}
                        subtext={`${safePercent(reportData.declined_count, reportData.total_applicants)}% of total`}
                        icon={HighlightOffIcon}
                        color={theme.palette.error.main}
                        tooltip="Candidates marked as 'Declined' or failed screening."
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        title="Total Pending" 
                        value={reportData.pending_count}
                        subtext={`${safePercent(reportData.pending_count, reportData.total_applicants)}% of total`}
                        icon={HourglassEmptyIcon}
                        color={theme.palette.warning.main}
                        tooltip="Candidates currently under review."
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        title="Total Accepted" 
                        value={reportData.approved_count}
                        subtext={`${safePercent(reportData.approved_count, reportData.total_applicants)}% of total`}
                        icon={ThumbUpAltIcon}
                        color={theme.palette.success.main}
                        tooltip="Candidates marked as 'Approved'."
                    />
                </Grid>
            </Grid>

            <SectionHeader title="Candidate Quality & Engagement" />
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, bgcolor: 'background.paper' }}>
                <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={4} display="flex" flexDirection="column" alignItems="center">
                        <AnimatedMetricDisplay 
                            score={reportData.avg_skills_match_percent / 100} 
                            label="skillsMatch" 
                            title="Skills Match" 
                            size="medium"
                        />
                    </Grid>
                    <Grid item xs={12} md={4} display="flex" flexDirection="column" alignItems="center">
                        <AnimatedMetricDisplay 
                            score={reportData.screening_pass_rate / 100} 
                            label="passRate" 
                            title="Qualifications" 
                            size="medium"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Average Qualification Score</Typography>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <WorkspacePremiumIcon color="action" fontSize="small" />
                                    <Box>
                                        <Typography variant="h5" fontWeight={600}>
                                            {reportData.avg_qualification_score || 0}<Typography component="span" variant="body1" color="text.secondary">/10</Typography>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">Based on scorecard data</Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <Divider />
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Peak Application Activity</Typography>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                    <CalendarTodayIcon color="action" fontSize="small" />
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight={600}>{reportData.top_day}</Typography>
                                        <Typography variant="caption" color="text.secondary">{reportData.top_hours}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={3} sx={{ mt: 1 }}>
                
                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 3, height: '100%', borderRadius: 3 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={3}>
                            <PeopleIcon color="primary" />
                            <Typography variant="h6" fontWeight={600}>Demographics</Typography>
                        </Box>
                        <Stack spacing={2}>
                            <LinearMetric label="Female" value={safePercent(reportData.female_percent, 100)} total={100} color="#FF6D00" />
                            <LinearMetric label="Male" value={safePercent(reportData.male_percent, 100)} total={100} color="#1a73e8" />
                            <LinearMetric label="Other / Not Specified" value={safePercent(reportData.other_percent + reportData.undisclosed_percent, 100)} total={100} color="#9c27b0" />
                        </Stack>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 3, height: '100%', borderRadius: 3 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={3}>
                            <LocationOnIcon color="primary" />
                            <Typography variant="h6" fontWeight={600}>Top Locations</Typography>
                        </Box>
                        {reportData.top_locations && reportData.top_locations.length > 0 ? (
                            <Stack spacing={1}>
                                {reportData.top_locations.slice(0, 5).map((loc, index) => (
                                    <LinearMetric 
                                        key={index} 
                                        label={loc.location} 
                                        value={loc.count} 
                                        total={reportData.total_applicants} 
                                        color={theme.palette.secondary.main}
                                    />
                                ))}
                            </Stack>
                        ) : (
                            <Typography color="text.secondary" variant="body2">No location data available yet.</Typography>
                        )}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 3, height: '100%', borderRadius: 3 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={3}>
                            <SourceIcon color="primary" />
                            <Typography variant="h6" fontWeight={600}>Top Sources</Typography>
                        </Box>
                        {reportData.sources && reportData.sources.length > 0 ? (
                            <Stack spacing={1}>
                                {reportData.sources.slice(0, 5).map((source, index) => (
                                    <LinearMetric 
                                        key={index} 
                                        label={source.source} 
                                        value={source.applicants} 
                                        total={reportData.total_applicants} 
                                        color={theme.palette.success.main}
                                    />
                                ))}
                            </Stack>
                        ) : (
                            <Typography color="text.secondary" variant="body2">No source data available yet.</Typography>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}